import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Typography, Skeleton, Stack } from '@mui/material';
import React, { useContext } from 'react';
import { useSiteVarsFunctions } from '../../hooks/use-site-vars-functions';
import PremPlayersContext from '../../contexts/premplayers-context';
import { PlayerShirtImage } from '../player/player-shirt-image';
import { PlayerImage } from '../player/player-image';
import LoseIcon from '@mui/icons-material/HighlightOff';
import WinIcon from '@mui/icons-material/CheckCircleOutline';
import DrawIcon from '@mui/icons-material/RemoveCircleOutline';

export const AuctionResultsTable = ({ results }) => {
  const { getManagerName, getPlayerPositionById, getTeamById } = useSiteVarsFunctions();
  const { premPlayersData, isLoadingPremPlayersData } = useContext(PremPlayersContext);

  const sortedResults = [...results].sort((a, b) => {
    // Sort by window desc
    if (a.window !== b.window) {
      return b.window - a.window;
    }
    // Then by phase desc
    if (a.phase !== b.phase) {
      return b.phase - a.phase;
    }
    // Then by playerId desc
    if (a.playerId !== b.playerId) {
      return b.playerId - a.playerId;
    }
    // Then by bid (amount) desc
    return b.amount - a.amount;
  });

  const getPlayerData = (playerId) => {
    return premPlayersData.find(player => player.playerId === playerId);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 350 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '120px', padding: '6px 14px' }}>Manager</TableCell>
              <TableCell sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>Player</TableCell>
              <TableCell sx={{ padding: '6px 14px' }}></TableCell>
              <TableCell sx={{ padding: '6px 4px' }}>Bid</TableCell>
              <TableCell sx={{ padding: '6px 4px' }}>Result</TableCell>
              <TableCell sx={{ padding: '6px 14px' }}>Win</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingPremPlayersData && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Stack spacing={1}>
                    <Skeleton variant="rounded" width={"100%"} height={26} />
                    <Skeleton variant="rounded" width={"100%"} height={26} />
                    <Skeleton variant="rounded" width={"100%"} height={26} />
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {!isLoadingPremPlayersData && (() => {
              let previousPlayerId = null;
              return sortedResults.map((result, index) => {
                const player = getPlayerData(result.playerId);
                if (!player) return null;

                const displayPlayerDetails = result.playerId !== previousPlayerId;
                previousPlayerId = result.playerId;

                return (
                  <TableRow key={index} sx={displayPlayerDetails ? { borderTop: '2px solid rgba(164, 164, 164, 1)' } : {}}>
                    <TableCell sx={{ padding: '6px 14px' }}>{getManagerName(result.clubId)}</TableCell>
                    {displayPlayerDetails ? (
                      <>
                        <TableCell sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
                          <PlayerImage player={player} />
                        </TableCell>
                        <TableCell sx={{ padding: '6px 14px', paddingLeft: 1 }}>
                          <span>{player.lastName}</span>
                          <br />
                          <span style={{ fontWeight: 'bold' }}>{getPlayerPositionById(player.positionId).name_short}</span> {getTeamById(player.teamId).name_short}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}></TableCell>
                        <TableCell sx={{ padding: '6px 14px', paddingLeft: 1 }}></TableCell>
                      </>
                    )}
                    <TableCell sx={{ padding: '6px 4px' }}>£{result.amount}</TableCell>
                    <TableCell sx={{ textAlign: 'center', padding: '6px 4px' }}>
                      {result.status === 'win' && <WinIcon sx={{ color: 'green' }} />}
                      {result.status === 'lose' && <LoseIcon sx={{ color: 'red' }} />}
                      {result.status === 'draw' && <DrawIcon sx={{ color: 'amber' }} />}
                    </TableCell>
                    <TableCell sx={{ padding: '6px 14px' }}>{result["windowId+phaseId"]}</TableCell>
                  </TableRow>
                );
              });
            })()}
          </TableBody>
        </Table>
      </TableContainer>
      {results.length === 0 && (
        <Typography variant="subtitle1" sx={{ mt: 2, textAlign: 'center' }}>
          No auction results to display.
        </Typography>
      )}
    </Box>
  );
};